import {
    CreatorProfileImageSize,
    CreatorProfileTopic,
    GetCreatorProfileResponse
} from '../types';
import { LitElement, css, html } from 'lit';
import { customElement, query, state } from 'lit/decorators';
import { debounce, isDefined, replaceStateWithPageName, undefinedIfEmpty } from '../helpers/utils';
import { getCreatorProfile, getTopicsFromCreatorProfile } from '../helpers/api/creator-profile-api';

import { Dialog } from '@material/mwc-dialog';
import { TextField } from '@material/mwc-textfield';
import { detectMobileViewport } from '../helpers/device';
import { getQueryStringParameters } from '../helpers/urls';

enum TopicSortTypes {
    NEWEST = 'Newest',
    OLDEST = 'Oldest',
    ALPHABETICAL = 'Alphabetical'
};

@customElement('creator-profile-page')
export class CreatorProfilePage extends LitElement {
    amplitude_callsite: string = 'Creator Profile Page';

    @query('#searchText')
    private readonly searchTextElement!: TextField;



    @query('#addMapsModalComponent')
    addMapsModalComponent?: Dialog;

    @state()
    creatorProfile?: GetCreatorProfileResponse;

    @state()
    allCreatorTopics: CreatorProfileTopic[] = [];

    @state()
    creatorTopics: CreatorProfileTopic[] = [];

    @state()
    numPinned: number = 0;

    async firstUpdated() {
        await this.startup();
    }

    private async startup() {
        const queryString = getQueryStringParameters();
        const profileId = queryString.get('profile_id');

        if (profileId) {
            this.creatorProfile = await getCreatorProfile(this.amplitude_callsite, profileId);

            // the topics on creator profile only have ids - we fetch full topics here, and then need to create new models that merge the two
            const topics = await getTopicsFromCreatorProfile(this.amplitude_callsite, this.creatorProfile._id.$oid);

            const creatorTopics = this.creatorProfile.topics.map(profileTopic => {
                const populatedTopic = topics.find(topic => topic._id.$oid === profileTopic.topic_id.$oid);
                if (isDefined(populatedTopic)) {
                    return {
                        topic: populatedTopic,
                        is_pinned: profileTopic.is_pinned
                    };
                }

                return null; // Return null if the topic is not found or is private
            }).filter(isDefined);

            this.numPinned = creatorTopics.filter(topic => topic.is_pinned).length;
            const pinnedFirstCreatorTopics = this.sortPinsFirst(creatorTopics);
            const sortedCreatorTopics = this.sortByTopic((Object.keys(TopicSortTypes) as Array<keyof typeof TopicSortTypes>)[0], pinnedFirstCreatorTopics);
            this.creatorTopics = sortedCreatorTopics;
            this.allCreatorTopics = sortedCreatorTopics;
            // set url back to creator profile short url
            replaceStateWithPageName(this.creatorProfile.link_set.public_link, `${this.creatorProfile.name ?? ''}: Proxi Maps`);
        } else {
            window.location.assign('/#404');
        }
        window.dispatchEvent(new CustomEvent('page-content-ready'));
    }

    private sortPinsFirst(creatorTopics: CreatorProfileTopic[]) {
        const sortedTopics = creatorTopics.sort((creatorTopicA, creatorTopicB) => {
            if(creatorTopicA.is_pinned) {
                return -1;
            }
            return 1;
        });

        return sortedTopics;
    }

    private readonly onSearchText = debounce(this.searchTextInternal, 200);

    private searchTextInternal() {
        const searchText = undefinedIfEmpty(this.searchTextElement.value?.trim().toLocaleLowerCase());
        if (searchText) {
            this.creatorTopics = this.allCreatorTopics.filter(topic => {
                return topic.topic.name.toLocaleLowerCase().includes(searchText);
            });
        } else {
            this.creatorTopics = this.allCreatorTopics;
        }
    }


    private sortByTopic(sortType: keyof typeof TopicSortTypes, creatorTopics: CreatorProfileTopic[]) {
        // we dont want to sort topics that are pinned!
        const topicsToSort = creatorTopics.slice(this.numPinned, creatorTopics.length);
        const pinnedTopics = creatorTopics.slice(0, this.numPinned);

        let sortedTopics: CreatorProfileTopic[] = [];

        if (sortType === 'ALPHABETICAL'){
            sortedTopics = topicsToSort.sort((creatorTopicA, creatorTopicB) => {
                const textA = creatorTopicA.topic.name.toUpperCase();
                const textB = creatorTopicB.topic.name.toUpperCase();
                return (textA < textB) ? -1 : (textA > textB) ? 1 : 0;

            });
        } else if (sortType === 'NEWEST') {
            sortedTopics = topicsToSort.sort((creatorTopicA, creatorTopicB) => {
                const createdAtA = creatorTopicA.topic.created_at;
                const createdAtB = creatorTopicB.topic.created_at;
                return (createdAtA > createdAtB) ? -1 : (createdAtA < createdAtB) ? 1 : 0;
            });
        } else if (sortType === 'OLDEST') {
            sortedTopics = topicsToSort.sort((creatorTopicA, creatorTopicB) => {
                const createdAtA = creatorTopicA.topic.created_at;
                const createdAtB = creatorTopicB.topic.created_at;
                return (createdAtA < createdAtB) ? -1 : (createdAtA > createdAtB) ? 1 : 0;
            });
        }

        return [...pinnedTopics, ...sortedTopics];
    }

    private dropdownSort(sortType: keyof typeof TopicSortTypes) {
        this.creatorTopics = this.sortByTopic(sortType, this.creatorTopics);
    }

    protected render() {
        return html`
            <div class="creator-profile__heading-space"></div>
            <div class="creator-profile__header_image_holder">
                <img src="${this.creatorProfile?.cover_image ?? 'https://images.pexels.com/photos/8828425/pexels-photo-8828425.jpeg'}" class="creator-profile__background" />
            </div>
            <div class="creator-profile__page-container">
                <div class="creator-profile__main-content">
                    <creator-profile-card
                        .creatorProfile=${this.creatorProfile}
                        .useVerticalLayout=${detectMobileViewport()}
                        .imageSize=${detectMobileViewport() ? CreatorProfileImageSize.MEDIUM : CreatorProfileImageSize.LARGE}>
                    </creator-profile-card>

                    <div class="creator-profile__topics-wrapper">
                        <!-- <div class="creator-profile__topic-controls">
                            <span>
                                <proxi-textfield
                                    id="searchText"
                                    class="creator-profile__search"
                                    helper="Search for your map"
                                    icon="search"
                                    outlined
                                    @input=""></proxi-textfield>
                            </span>

                            <span class="creator-profile__topic-sort-wrapper desktopOnly">
                                <span class="creator-profile__topic-sort">
                                    <mwc-icon>swap_vert</mwc-icon>
                                    Sort by:
                                    <proxi-selectfield outlined class="creator-profile__topic-sort-selector">

                                    </proxi-selectfield>
                                </span>
                            </span>
                        </div> -->

                        <div class="creator-profile__maps-count mobileOnly">
                            Maps (${this.creatorTopics.length})
                        </div>
                        <div class="creator-profile__topics">
                            ${this.creatorTopics?.map(creatorTopic => {
                                return html`<div class="column-item">
                                    <backpack-card
                                        .creatorProfile=${this.creatorProfile}
                                        .topic=${creatorTopic.topic}
                                        ?goToDetailPage=${true}
                                        ?inCarousel=${true}
                                        ?isPinned=${creatorTopic.is_pinned}>
                                    </backpack-card></div>`;
                            })}
                        </div>
                    </div>
                </div>
            </div>

            <error-toast id="errorToast"></error-toast>
        `;
    }

    static get styles() {
        return css`
            .creator-profile__page-container {
                display: flex;
            }

            .creator-profile__search {
                padding-right: 10px;
            }

            .creator-profile__topic-sort-selector {
                padding-left: 10px;
            }

            .creator-profile__topic-count {
                border-bottom: 1px solid var(--primary-text-color);
            }

            .creator-profile__topic-sort-wrapper {
                display: flex;
                align-self: flex-start;
            }

            .creator-profile__topic-sort {
                align-items: center;
                display: flex;
            }

            .creator-profile__topic-sort-title {
                align-self: center;
            }

            .creator-profile__subheader {
                margin-bottom: 14px;
            }

            .creator-profile__main-content {
                z-index: 1;
            }

            .creator-profile__topics-wrapper {
                margin-top: 30px;
            }

            .column-item {
                width: 25%;
                min-width: 150px;
                padding: 10px;
                box-sizing: border-box;
            }

            @media (min-width: 551px) {
                .desktopOnly {
                    display: block;
                }

                .mobileOnly {
                    display: none;
                }

                .creator-profile__topics {
                    padding-bottom: 3.5rem;
                    width: 100%;
                    box-sizing: border-box;
                    margin: 0px -10px;
                    display: flex;
                    flex-wrap: wrap;
                    overflow-y: auto; // DO NOT REMOVE - this allows scrolling on mobile
                }

                .creator-profile__background {
                    width: 100%;
                    object-fit: cover;
                }

                .creator-profile__header_image_holder {
                    height: 175px;
                    width: 100%;
                    position: relative;
                    overflow: hidden;
                    display: flex;
                }

                .creator-profile__header {
                    display: flex;
                }

                .creator-profile__main-content {
                    margin: auto;
                    width: 70%;
                    margin-top: 20px;
                }

                .creator-profile__topic-controls {
                    display: flex;
                    justify-content: space-between;
                    font-size: 16px;
                }
            }

            @media (max-width: 550px) {
                .desktopOnly {
                    display: none;
                }

                .mobileOnly {
                    display: block;
                }

                .creator-profile__page-container {
                    padding: 10px;
                    overflow-y: scroll; // DO NOT REMOVE - this allows scrolling on mobile
                }

                .creator-profile__maps-count {
                    margin-left: 10px;
                    font-weight: 700;
                    font-size: 15px;
                }

                .creator-profile__topic-sort-selector {
                    width: 75%;
                }

                .creator-profile__search {
                    width: 95%;
                    padding-left: 10px;
                }

                .creator-profile__background {
                    width: 100%;
                }

                .creator-profile__header_image_holder {
                    height: 80px;
                    width: 100%;
                    position: relative;
                    overflow: hidden;
                    display: flex;
                    align-items: center;
                }


                .creator-profile__header {
                    display: block;
                    text-align: center;
                    padding: 0 10px;
                }

                .creator-profile__main-content {
                    width: 100%;
                    min-height: 100vh;
                }

                .column-item {
                    width: 50%;
                    min-width: 150px;
                }
            }
        `;
    }
}
